import { PencilSquare, Xmark } from '@byway/icons';
import clsx from 'clsx';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Button } from '~/components/Button';
import { usePlaygroundActions, usePlaygroundDateType, usePlaygroundPreferences } from '~/stores/playgroundStore';
import { formatExactDate } from '~/utils/dateHelpers/dateHelpers';
import styles from './PlaygroundDateSelector.module.css';

type PlaygroundDateSelectorProps = {
  onChange: (
    value: {
      type: 'nights' | 'calendar';
      value: { startDate: string; endDate: string } | number;
    } | null
  ) => void;
  dataUaClick?: string;
  // Opt-out of opening the global playground drawer. Consumers nested inside
  // their own modal (e.g. NavSearch) supply this to avoid two `<Drawer isOpen={isDrawerOpen && drawerItem === 'dates'}>`
  // subscribers fighting over the same global flag.
  onClickOverride?: () => void;
};

export const PlaygroundDateSelector = ({
  onChange,
  dataUaClick = 'playground_when_click',
  onClickOverride
}: PlaygroundDateSelectorProps) => {
  const t = useTranslations();
  const { openDrawer, setStartDate, setEndDate, setNights, setDatesTab } = usePlaygroundActions();
  const { nights, startDate, endDate } = usePlaygroundPreferences();
  // NOTE (G2-3999): this already read `params.lang`, but map sat outside [lang] so it was always
  // undefined and fell back to 'en'. Now map is under [lang] it receives the real locale, which
  // changes the rendered format for en-GB users: 'Sep 14, 26' → '14 Sept 26'. Correct, but it is a
  // visible change activated by the route move rather than written here — needs a copy/design glance.
  const params = useParams();
  const dateType = usePlaygroundDateType();
  // Track previous values to avoid unnecessary updates
  const prevValues = useRef({
    dateType,
    nights,
    startDate,
    endDate
  });

  const hasDates = nights || startDate;

  // Sync Zustand state to form whenever relevant values change - this needed for popover in nav to work (because it portals). Either this or we lift our form provider to the top of the app
  useEffect(() => {
    // Only update if values have actually changed
    const valueChanged =
      prevValues.current.dateType !== dateType ||
      prevValues.current.nights !== nights ||
      prevValues.current.startDate !== startDate ||
      prevValues.current.endDate !== endDate;

    if (valueChanged) {
      prevValues.current = { dateType, nights, startDate, endDate };

      if (dateType === 'nights' && nights) {
        onChange({ type: 'nights', value: nights });
      } else if (dateType === 'calendar' && startDate && endDate) {
        onChange({ type: 'calendar', value: { startDate, endDate } });
      } else if (!nights && !startDate && !endDate) {
        onChange(null);
      }
    }
  }, [onChange, dateType, nights, startDate, endDate]);

  const handleClick = () => {
    setDatesTab(dateType === 'nights' ? 'nights' : 'calendar');
    if (onClickOverride) {
      onClickOverride();
      return;
    }
    openDrawer('dates');
  };

  const closeHandler = () => {
    setStartDate(null);
    setEndDate(null);
    setNights(null);
  };

  const emptyButtonText = <span className={styles.emptyButtonText}>{t('playground.duration_prompt')}</span>;

  const calendarTextValue =
    startDate && endDate ? formatExactDate(startDate!, endDate!, (params?.lang as string) ?? 'en') : emptyButtonText;
  const nightsTextValue = nights ? t('journey.nights_count', { count: nights }) : emptyButtonText;

  const buttonText = dateType === 'nights' ? nightsTextValue : calendarTextValue;

  return (
    <div className={styles.wrapper}>
      <AriaButton data-ua-click={dataUaClick} onPress={handleClick} className={clsx(styles.container, styles.button)}>
        <span>{buttonText}</span>
      </AriaButton>
      <div className={styles.innerButton}>
        {hasDates ? (
          <Button variant="icon" onPress={closeHandler} ariaLabel={t('aria.clear_dates')}>
            <Xmark size="large" decorative />
          </Button>
        ) : (
          <div className={styles.fakeButton} onClick={handleClick}>
            <PencilSquare size="large" decorative />
          </div>
        )}
      </div>
    </div>
  );
};
